import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {FlightMode} from "../FlightMode";
import {FlightModeGameAssets} from "../FlightModeGameAssets";
import {gsap} from "gsap";
import {BasedCountUp} from "./BasedCountUp";
import {SpineGSAPWrapper} from "../misc/SpineGSAPWrapper";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {SimplePresentationRunnerController} from "../presentations/SimplePresentationRunner";

export class ExtraSpin extends PIXI.Container {

    yesButton:ContinueButton;
    noButton:ContinueButton;
    yesButtonCallback:Function;
    noButtonCallback:Function;
    promptBox:PIXI.Sprite;
    text:PIXI.Text;
    controller:SimplePresentationRunnerController;
    rtpSticker:PIXI.Sprite;
    replayClick:TimelineSprite;
    promptSpine:PIXI.spine.Spine;
    costText:PIXI.Text;

    constructor() {
        super();

        const translations = FlightMode.apiPlugin.translations;
        const assetLoader = FlightMode.assetLoader;

        this.yesButton = new ContinueButton(translations.translate("YES"));
        this.yesButton.setSize(240, 120);
        this.yesButtonCallback = this.clickedYes;
        this.yesButton.addClickCallback(() => this.yesButtonCallback());
        this.yesButton.position.set(210, 514);
        this.yesButton.pivot.set(this.yesButton.width * 0.5, this.yesButton.height * 0.5);
        this.yesButton.enable(false);
        this.yesButton.alpha = 0;

        this.noButton = new ContinueButton(translations.translate("NO"));
        this.noButton.setSize(240, 120);
        this.noButtonCallback = this.clickedNo;
        this.noButton.addClickCallback(() => this.noButtonCallback());
        this.noButton.position.set(510, 514);
        this.noButton.pivot.set(this.noButton.width * 0.5, this.noButton.height * 0.5);
        this.noButton.enable(false);
        this.noButton.alpha = 0;

        this.promptBox = new PIXI.Sprite(assetLoader.getTextures("Info_Window")[0]);
        this.promptBox.position.set(360, 360);
        this.promptBox.anchor.set(0.5, 0.5);

        this.promptSpine = new PIXI.spine.Spine(assetLoader.getSpineAsset(FlightModeGameAssets.EXTRA_SPIN));
        this.promptSpine.position.set(360, 360);

        const extraSpinCost = FlightMode.model.extraSpinCost;
        const textStyle = new PIXI.TextStyle({
            fill: "#ffffff",
            fontFamily: "Helvetica",
            fontSize: 60,
            fontStyle: "oblique",
            fontWeight: "bolder",
            lineJoin: "round",
            strokeThickness: 0,
            wordWrapWidth: 600,
            wordWrap: true,
            breakWords: true,
            dropShadow: true,
            dropShadowAngle: 1.5,
            dropShadowBlur: 3,
            dropShadowDistance: 4,
            "padding": 12,
        });
        this.text = new PIXI.Text(translations.translate("Buy Extra Spin!"), textStyle);
        this.text.position.set(360, 260);
        this.text.anchor.set(0.5, 0.5);
        this.text.visible = false;

        this.costText = new PIXI.Text(FlightMode.apiPlugin.currency.format(FlightMode.model.extraSpinCost), textStyle);
        this.costText.position.set(360, 360);
        this.costText.scale.set(1.2, 1.2);
        this.costText.anchor.set(0.5, 0.5);
        this.costText.visible = false;

        this.alpha = 1;

        this.replayClick = new TimelineSprite(FlightMode.assetLoader.getTextures(FlightModeGameAssets.REPLAY_CLICK));
        this.replayClick.blendMode = PIXI.BLEND_MODES.ADD;
        this.replayClick.scale.set(2,2);
        this.replayClick.anchor.set(0.5, 0.5);
        this.replayClick.position.set(360, 514);
        this.replayClick.hide();

        this.addChild(this.promptSpine);
        this.addChild(this.text);
        this.addChild(this.costText)
        this.addChild(this.noButton);
        this.addChild(this.yesButton);
        this.addChild(this.replayClick);
    }

    enable(controller:SimplePresentationRunnerController):gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this, {duration: 0.25}))
            .add(() => {
                FlightMode.soundPlayer.playSound("popUp");
            })
            .add(SpineGSAPWrapper.createTimeline(this.promptSpine, 0, "In"))
            .add(() => {
                const idleTl = gsap.timeline()
                    .add(() => {
                        if (!this.text.visible)
                            idleTl.kill();
                        else
                            this.promptSpine.state.setAnimation(0, "Idle_Attention", false);
                    }, 3);
                idleTl.repeat(-1);

                this.costText.text = FlightMode.apiPlugin.currency.format(FlightMode.model.extraSpinCost);
                this.controller = controller;
                this.promptBox.visible = true;
                this.text.visible = true;
                this.costText.visible = true;
                this.yesButton.enable(true);
                this.noButton.enable(true);
                const extraSpinCost = FlightMode.model.extraSpinCost;
                this.text.text = FlightMode.apiPlugin.translations.translate("Buy Extra Spin!");
            })
            .add(gsap.to(this.noButton, {duration: 0.2, alpha: 1}), '<')
            .add(gsap.to(this.yesButton, {duration: 0.2, alpha: 1}), '<')
    }

    clickedYes():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                FlightMode.soundPlayer.playSound("popUpYes");
                this.text.visible = false;
                this.costText.visible = false;
                this.noButton.enable(false);
                this.yesButton.enable(false);
                const newWinAmount = FlightMode.model.accumulatedRoundWin - FlightMode.model.extraSpinCost;
                FlightMode.keypadPlugin.setWin(newWinAmount, true, FlightMode.isWinBelowStake(newWinAmount));
            })
            .add(gsap.to(this.noButton, {duration: 0.2, alpha: 0}), '<')
            .add(gsap.to(this.yesButton, {duration: 0.2, alpha: 0}), '<')
            .add(SpineGSAPWrapper.createTimeline(this.promptSpine, 0, "Out"))
            .add(() => {
                FlightMode.apiPlugin.betHandler.zeroBet({playExtraSpin:"yes"})
                this.controller.finish();
            })
    }

    clickedNo():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                FlightMode.soundPlayer.playSound("popUpNo");
                this.text.visible = false;
                this.costText.visible = false;
                this.noButton.enable(false);
                this.yesButton.enable(false);
            })
            .add(gsap.to(this.noButton, {duration: 0.2, alpha: 0}), '<')
            .add(gsap.to(this.yesButton, {duration: 0.2, alpha: 0}), '<')
            .add(SpineGSAPWrapper.createTimeline(this.promptSpine, 0, "Out"))
            .add(() => {
                FlightMode.apiPlugin.betHandler.zeroBet({playExtraSpin:"no"})
                this.controller.finish();
            })
    }

    replay(controller:SimplePresentationRunnerController):gsap.core.Timeline {
        const played = FlightMode.model.nextPlayerInteraction?.playExtraSpin == "yes";
        return gsap.timeline()
            .add(() => {
                FlightMode.soundPlayer.playSound("popUp");
            })
            .add(gsap.to(this, {duration: 0.25}))
            .add(SpineGSAPWrapper.createTimeline(this.promptSpine, 0, "In"))
            .add(() => {
                this.controller = controller;
                this.promptBox.visible = true;
                this.text.visible = true;
                this.costText.visible = true;
                this.yesButton.enable(false);
                this.noButton.enable(false);
                const extraSpinCost = FlightMode.model.extraSpinCost;
                this.costText.text = FlightMode.apiPlugin.currency.format(extraSpinCost);
            })
            .add(gsap.to(this.noButton, {duration: 0.2, alpha: 1}), '<')
            .add(gsap.to(this.yesButton, {duration: 0.2, alpha: 1}), '<')
            .add(() => {
                FlightMode.soundPlayer.playSound(played ? "popUpYes" : "popUpNo");
                this.replayClick.position.x = played ? 210 : 510;
                this.replayClick.show();
                this.replayClick.playLoop();
            }, '>1')
            .add(() => {
                this.text.visible = false;
                this.costText.visible = false;
                this.replayClick.hide();
                this.replayClick.stopLoop();
                this.replayClick.setFrame(0);
                if (played) {
                    const newWinAmount = FlightMode.model.accumulatedRoundWin - FlightMode.model.extraSpinCost;
                    FlightMode.keypadPlugin.setWin(newWinAmount, true, FlightMode.isWinBelowStake(newWinAmount));
                }
            }, `>${this.replayClick.totalDuration}`)
            .add(gsap.to(this.noButton, {duration: 0.2, alpha: 0}), '<')
            .add(gsap.to(this.yesButton, {duration: 0.2, alpha: 0}), '<')
            .add(SpineGSAPWrapper.createTimeline(this.promptSpine, 0, "Out"))
            .add(() => {
                FlightMode.apiPlugin.betHandler.zeroBet({playExtraSpin:played ? "yes" : "no"});
                this.controller.finish();
            })
    }
}